/**
 * Question files — the Decision Compiler's first-class objects.
 *
 * Layout: `agentic/<domain>/<area[/<subarea>]/_questions/<id>.md`.
 * This module mirrors `tools/questions.py` so console and nightly engine
 * agree byte-for-byte on the format (parse + answer-fill + status edit).
 *
 * Answer contract: the file ships a skeleton block
 *   `- choice:` / `- text:` / `- delegated:` / `- waived:`
 * inside `<!-- answer:begin -->` … `<!-- answer:end -->`. Answers FILL the
 * skeleton lines; the parser takes the FIRST non-empty value per key, so an
 * appended line works too and a later empty skeleton line never wins.
 */

export type QuestionStatus =
  | "open"
  | "answered"
  | "delegated"
  | "waived"
  | "closed"
  | "superseded";

export interface QuestionOption {
  key: string;
  label: string;
  consequence: string;
  recommended: boolean;
}

export interface Question {
  path: string;
  id: string;
  node: string;
  domain: string;
  /** Node path minus `agentic/<domain>/` ("" when the question sits at domain level). */
  area: string;
  status: QuestionStatus;
  title: string;
  whyNow: string;
  /** Mandatory facts block (contract v3.1) — the founder decides from the card. */
  context: string;
  recommendation: string;
  options: QuestionOption[];
  links: string[];
  /** Practical person tags (multi) — used for filtering, nothing else. */
  persons: string[];
  /** YYYY-MM-DD, founder-only (console writes it); while in the future the
   * question is snoozed — skipped by the deck and the conductor. */
  snoozeUntil: string;
  created: string;
  generatedBy: string;
  /** `consolidation` marks a founder approval for a domain consolidation plan
   * (written by the nightly runner; the Deck shows it with its own color). */
  kind?: string;
  answerChoice: string;
  answerText: string;
  delegated: boolean;
  waived: boolean;
  supersededBy: string;
  raw: string;
}

const STATUSES: readonly QuestionStatus[] = [
  "open",
  "answered",
  "delegated",
  "waived",
  "closed",
  "superseded",
];

const OPTION_RE = /^- \[[ xX]\] \*\*([A-Z0-9]+)\*\*[ \t]*(.*)$/;
const ARROW_RE = /^[ \t]*(?:>[ \t]*)?→[ \t]*(.*)$/;

function asStatus(value: string): QuestionStatus {
  return (STATUSES as readonly string[]).includes(value)
    ? (value as QuestionStatus)
    : "open";
}

/** `---` front-matter → body; `null` when the fences are missing. */
export function splitFrontmatter(
  text: string,
): { fm: string; body: string } | null {
  if (!text.startsWith("---")) return null;
  const end = text.indexOf("\n---", 3);
  if (end === -1) return null;
  const fm = text.slice(3, end);
  let body = text.slice(end + 4);
  if (body.startsWith("\r")) body = body.slice(1);
  if (body.startsWith("\n")) body = body.slice(1);
  return { fm, body };
}

/** Minimal YAML-subset reader for the keys question files actually use. */
function frontmatterValues(fm: string): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  let list: string[] | null = null;
  for (const line of fm.split(/\r?\n/)) {
    const item = /^\s+-\s+(.+)$/.exec(line);
    if (item && list) {
      list.push(item[1].trim().replace(/^"|"$/g, ""));
      continue;
    }
    list = null;
    const m = /^([A-Za-z0-9_-]+)\s*:\s*(.*)$/.exec(line);
    if (!m) continue;
    const value = m[2].trim();
    if (value === "") {
      list = [];
      out[m[1]] = list;
    } else {
      out[m[1]] = value.replace(/^"|"$/g, "");
    }
  }
  return out;
}

function str(value: string | string[] | undefined): string {
  return typeof value === "string" ? value : "";
}

function list(value: string | string[] | undefined): string[] {
  return Array.isArray(value) ? value : [];
}

/** Full paragraph after a `**Marker:**` line (continuation lines joined). */
function paragraphAfter(body: string, marker: string): string {
  const idx = body.indexOf(marker);
  if (idx === -1) return "";
  const rest = body.slice(idx + marker.length);
  const out: string[] = [];
  for (const raw of rest.split(/\r?\n/)) {
    const line = raw.trim();
    if (line === "") break;
    if (out.length > 0 && /^(\*\*|- |#|`|>)/.test(line)) break;
    out.push(line);
  }
  return out.join(" ").trim();
}

export function parseQuestion(path: string, text: string): Question | null {
  const split = splitFrontmatter(text);
  if (!split) return null;
  const fm = frontmatterValues(split.fm);
  const id = str(fm.id);
  const node = str(fm.node);
  if (!id || !node) return null;
  const body = split.body;

  const titleMatch = /^#\s+(.+?)\s*$/m.exec(body);
  const title = titleMatch
    ? titleMatch[1].replace(/`[^`]*`/g, "").trim()
    : id;

  const options: QuestionOption[] = [];
  const lines = body.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const m = OPTION_RE.exec(lines[i]);
    if (!m) continue;
    const key = m[1];
    let label = m[2].trim();
    const recommended = /\(recommended/i.test(label);
    label = label.replace(/\*?\(recommended[^)]*\)\*?/gi, "").trim();
    const consequences: string[] = [];
    for (let j = i + 1; j < lines.length; j++) {
      if (OPTION_RE.test(lines[j])) break;
      const arrow = ARROW_RE.exec(lines[j]);
      if (arrow) consequences.push(arrow[1].trim());
    }
    options.push({
      key,
      label,
      consequence: consequences.join(" "),
      recommended,
    });
  }

  let answerChoice = "";
  let answerText = "";
  let delegated = false;
  let waived = false;
  const ab = body.indexOf("<!-- answer:begin -->");
  const ae = body.indexOf("<!-- answer:end -->");
  if (ab !== -1 && ae > ab) {
    const block = body.slice(ab + "<!-- answer:begin -->".length, ae);
    for (const line of block.split(/\r?\n/)) {
      const m = /^\s*-\s*([a-z_]+)\s*:\s*(.*)$/.exec(line);
      if (!m) continue;
      const value = m[2].trim().replace(/^"|"$/g, "");
      const truthy = /^(yes|true|1|ja)$/i.test(value);
      if (m[1] === "choice" && value && !answerChoice) answerChoice = value;
      else if (m[1] === "text" && value && !answerText) answerText = value;
      else if (m[1] === "delegated" && truthy) delegated = true;
      else if (m[1] === "waived" && truthy) waived = true;
    }
  }

  const nodeParts = node.split("/");
  const domain = nodeParts[1] ?? "";
  const area = nodeParts.slice(2).join("/");

  return {
    path,
    id,
    node,
    domain,
    area,
    status: asStatus(str(fm.status)),
    title,
    whyNow: paragraphAfter(body, "**Why now:**"),
    context: paragraphAfter(body, "**Kontext:**"),
    recommendation: paragraphAfter(body, "**Recommendation:**"),
    options,
    links: list(fm.links),
    persons: list(fm.persons),
    snoozeUntil: str(fm.snooze_until),
    created: str(fm.created),
    generatedBy: str(fm.generated_by),
    kind: str(fm.kind),
    answerChoice,
    answerText,
    delegated,
    waived,
    supersededBy: str(fm.superseded_by),
    raw: text,
  };
}

/** The option the founder would pick (first recommended, else "A"). */
export function recommendedKey(q: Question): string {
  return q.options.find((o) => o.recommended)?.key ?? q.options[0]?.key ?? "A";
}

export function optionLabel(q: Question, key: string): string {
  return q.options.find((o) => o.key === key)?.label ?? "";
}

/** One-line summary of the recorded answer (for lists and rows). */
export function answerSummary(q: Question): string {
  if (q.waived) return "Never — not relevant";
  if (q.delegated) return "Recommendation stands";
  if (q.answerChoice) {
    const label = optionLabel(q, q.answerChoice);
    return label ? `${q.answerChoice} — ${label}` : q.answerChoice;
  }
  if (q.answerText) return q.answerText;
  return "";
}

// ---------------------------------------------------------------------------
// serialisation (writing back to GitHub)
// ---------------------------------------------------------------------------

/** Replace the front-matter `status:` line (surgical, content untouched). */
export function withStatus(text: string, status: QuestionStatus): string {
  return text.replace(/^status:\s*\S+\s*$/m, `status: ${status}`);
}

export interface AnswerPatch {
  choice?: string;
  text?: string;
  delegated?: boolean;
  waived?: boolean;
}

/** Fill the answer skeleton lines (only when still empty). */
export function withAnswerFill(text: string, patch: AnswerPatch): string {
  let out = text;
  if (patch.choice !== undefined) {
    out = out.replace(
      /^([ \t]*-[ \t]*choice:[ \t]*)$/m,
      `- choice: ${patch.choice}`,
    );
  }
  if (patch.text !== undefined) {
    out = out.replace(/^([ \t]*-[ \t]*text:[ \t]*)$/m, `- text: ${patch.text}`);
  }
  if (patch.delegated) {
    out = out.replace(
      /^([ \t]*-[ \t]*delegated:[ \t]*)$/m,
      "- delegated: yes",
    );
  }
  if (patch.waived) {
    out = out.replace(/^([ \t]*-[ \t]*waived:[ \t]*)$/m, "- waived: yes");
  }
  return out;
}

/** Convenience: fill the answer + flip the status in one edit. */
export function answeredContent(text: string, patch: AnswerPatch): string {
  return withStatus(withAnswerFill(text, patch), "answered");
}

/** Commit message for an answer write. */
export function answerMessage(q: Question, patch: AnswerPatch): string {
  const what = patch.delegated
    ? "delegated"
    : patch.waived
      ? "waived"
      : patch.choice
        ? patch.choice
        : "free text";
  return `answer: ${q.id} [${q.node.replace(/^agentic\//, "")}] ${what}`;
}

// ---------------------------------------------------------------------------
// person tags (practical metadata, nothing else — the founder filters by them)
// ---------------------------------------------------------------------------

const PERSONS_BLOCK_RE = /^persons:\s*\n(?:[ \t]+- .*\n?)*/m;

/** Set the `persons:` front-matter list (empty list removes the block). */
export function withPersons(text: string, ids: string[]): string {
  const clean = [...new Set(ids.map((s) => s.trim()))].filter((s) =>
    /^[a-z0-9-]+$/.test(s),
  );
  if (clean.length === 0) {
    return text.replace(PERSONS_BLOCK_RE, "");
  }
  const block = `persons:\n${clean.map((p) => `  - ${p}`).join("\n")}\n`;
  if (PERSONS_BLOCK_RE.test(text)) {
    return text.replace(PERSONS_BLOCK_RE, block);
  }
  const links = /^links:\s*\n(?:[ \t]+- .*\n?)*/m;
  if (links.test(text)) {
    return text.replace(links, (m) => `${m}${block}`);
  }
  return text.replace(/^(status:\s*\S+\s*)$/m, `$1\n${block}`.trimEnd());
}

/** Commit message for a tag write. */
export function personsMessage(q: Question, ids: string[]): string {
  const tags = ids.length > 0 ? ids.join(", ") : "none";
  return `tags: ${q.id} [${q.node.replace(/^agentic\//, "")}] ${tags}`;
}

// ---------------------------------------------------------------------------
// snooze (founder-only, reversible; status stays `open`)
// ---------------------------------------------------------------------------

/** "Until I say" — a date far in the future that no run will wait out. */
export const SNOOZE_SENTINEL = "9999-12-31";

/** Set the `snooze_until:` front-matter date ("" clears it). */
export function withSnooze(text: string, until: string): string {
  if (until) {
    if (/^snooze_until:/m.test(text)) {
      return text.replace(/^snooze_until:.*$/m, `snooze_until: ${until}`);
    }
    return text.replace(/^(status:\s*\S+\s*)$/m, `$1\nsnooze_until: ${until}`);
  }
  return text.replace(/\nsnooze_until:.*$/m, "");
}

/**
 * Re-ask: reset a decided question to Open. The answer skeleton is cleared,
 * `superseded_by` drops away, everything else (links, persons, context,
 * created) stays byte-for-byte. Staged like any other edit.
 */
export function reopenContent(text: string): string {
  let out = text.replace(/^status:\s*\S+\s*$/m, "status: open");
  out = out.replace(/^superseded_by:.*\r?\n/m, "");
  out = out.replace(
    /(<!--\s*answer:begin\s*-->)[\s\S]*?(<!--\s*answer:end\s*-->)/,
    "$1\n- choice:\n- text:\n- delegated:\n- waived:\n$2",
  );
  return out;
}

export function isSnoozed(
  q: Question,
  today: string = new Date().toISOString().slice(0, 10),
): boolean {
  return q.snoozeUntil !== "" && q.snoozeUntil > today;
}

/** Snooze target date N days from today. */
export function snoozeDate(days: number, today: Date = new Date()): string {
  const d = new Date(today);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// the five status words the founder sees
// ---------------------------------------------------------------------------

export type StatusWord =
  | "Open"
  | "Snoozed"
  | "Answered"
  | "Recorded"
  | "Never"
  | "Superseded";

export function statusWord(q: Question): StatusWord {
  switch (q.status) {
    case "open":
      return isSnoozed(q) ? "Snoozed" : "Open";
    case "answered":
    case "delegated":
      return "Answered";
    case "closed":
      return "Recorded";
    case "waived":
      return "Never";
    default:
      return "Superseded";
  }
}
